/*
 * The access token that comes with an order. The API gives it back once, when the order is placed, and reading the order
 * again (the confirmation page and its polling) needs it with the ref. It is kept here, apart from the stored order, because
 * the stored order is replaced by what the API returns when the order is read, and that doesn't repeat the token.
 */

const STORAGE_KEY = 'order-access';
const KEEP_FOR_MS = 24 * 60 * 60 * 1000;
const KEEP_MOST = 10;

type Entries = Record<string, { token: string; at: number }>;

const read = (): Entries => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
        return {};
    }
};

const write = (entries: Entries) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (e) {
        // storage can be full or blocked (private windows): the order is placed either way
    }
};

export const rememberOrderToken = (ref: string | undefined, token: string | undefined) => {
    if (!ref || !token) return;

    const now = Date.now();
    const entries = read();

    entries[ref] = { token, at: now };

    // only recent orders matter (the API only shows an order for about an hour and a half), and only a few
    const recent = Object.entries(entries)
        .filter(([, entry]) => entry && typeof entry.at === 'number' && now - entry.at < KEEP_FOR_MS)
        .sort(([, a], [, b]) => b.at - a.at)
        .slice(0, KEEP_MOST);

    write(Object.fromEntries(recent));
};

export const orderTokenFor = (ref: string | undefined): string | undefined => {
    if (!ref) return undefined;

    const entry = read()[ref];

    return entry && typeof entry.token === 'string' ? entry.token : undefined;
};
